import { builtEnvironment, IntakeDetectionResponse, isRunningLocal } from '@shared';
import { ApiClient } from './apiClient';
import getBaseUrl from '@shared/baseUrl';
import { API } from '@/api/types';

const origin = isRunningLocal() ? window.location.origin : getBaseUrl();
const env = isRunningLocal() ? 'dev' : builtEnvironment;
const waterlilyBaseURL = `${origin}/${env}/api`;

export const WaterlilyClient = new ApiClient({
  baseURL: waterlilyBaseURL,
  onUnauthorized: () => {
    WaterlilyClient.clearToken();
    window.location.href = '/auth/signout';
  },
});

export function createQueryKeys<T extends string>(resource: T) {
  return {
    all: [resource] as const,
    lists: () => [resource, 'list'] as const,
    list: <P extends Record<string, unknown>>(params: P) => [resource, 'list', { params }] as const,
    details: () => [resource, 'detail'] as const,
    detail: <K extends string>(id: K) => [resource, 'detail', id] as const,
  };
}

export const quotesApi = {
  query: (payload: FetchQuotesPayload) =>
    WaterlilyClient.post<QuotesApiResponse>('/quotes-query', payload),
};

export const clientKeys = createQueryKeys('client');

export const clientsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.ClientList>('/clients', params),
  getById: (id: string) => WaterlilyClient.get<API.Client>(`/client/${id}`),
  updateById: (id: string, data: Partial<API.Client>) =>
    WaterlilyClient.put<API.Client>(`/client/${id}`, data),
  deleteById: (id: string) => WaterlilyClient.delete<{ success: boolean }>(`/client/${id}`),
  getCalculations: (params: { page: number }) =>
    WaterlilyClient.get<API.Client>(`/client/calculations`),
};

export const advisorKeys = createQueryKeys('advisor');

export const advisorsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.AdvisorList>('/advisors', params),
  getById: (id: string) => WaterlilyClient.get<API.Advisor>(`/advisor/${id}`),
  updateById: (id: string, data: AdvisorProfile) =>
    WaterlilyClient.post<Advisor>(`/advisor-updates/${id}`, data),
  putById: (id: string, data: Advisor) => WaterlilyClient.put<Advisor>(`/advisor/${id}`, data),
};

export const authKeys = createQueryKeys('auth');

export const authApi = {
  postAdvisorExistsForPublic: (data: AdvisorExistsCheckRequest) =>
    WaterlilyClient.post<AdvisorExistsCheckResponse>('/public/advisor-exists', data),
};

export const stripeKeys = createQueryKeys('stripe');

export const stripeApi = {
  createCheckoutSession: (data: {
    advisorId: string;
    priceId?: string;
    planType?: 'trial' | 'monthly' | 'annual';
    returnPath?: string;
    uiMode?: 'embedded' | 'hosted';
  }) =>
    WaterlilyClient.post<{ clientSecret: string | null; url: string | null }>(
      '/stripe-subscription-checkout-session-url',
      data,
    ),
};

export const clientStripeApi = {
  createCheckoutSession: (data: {
    clientAuthUserId: string;
    returnPath?: string;
    uiMode?: 'embedded' | 'hosted';
  }) =>
    WaterlilyClient.post<{ clientSecret: string | null; url: string | null }>(
      '/public/client-auth-user-stripe-checkout-session',
      data,
    ),
};

export const advisorOnboardingKeys = {
  all: ['advisorOnboarding'] as const,
  status: (advisorId: string) => [...advisorOnboardingKeys.all, 'status', advisorId] as const,
};

export const advisorOnboardingApi = {
  getStatus: (advisorId: string) =>
    WaterlilyClient.get<AdvisorOnboardingStatus>(`/advisor-onboarding-status/${advisorId}`),

  /**
   * Mark a client-triggered onboarding step as complete.
   * Only accepts steps that cannot be computed from data: 'welcome', 'review-results'
   */
  markStepComplete: (data: { advisorId: string; step: AdvisorOnboardingClientTriggeredStep }) =>
    WaterlilyClient.post<{ success: boolean; step: string; completedSteps: string[] }>(
      `/advisor-onboarding-complete-step/${data.advisorId}`,
      { step: data.step },
    ),
};

export type SubscriptionSeatsResponse = {
  subscription: {
    stripeSubscriptionId: string;
    subscriptionIdRef: string;
    subscriptionStatus: string;
    totalSeats: number;
    ownerAdvisorId: string | null;
    unitPricePerSeat?: number | null;
    billingInterval?: string | null;
    trialEndDateTime?: Date | null;
    nextBillingDateTime?: Date | null;
    nextInvoiceAmount?: number | null;
    nextInvoiceDate?: Date | null;
  } | null;
  customer: {
    creditBalance?: number | null;
  } | null;
  seats: Array<
    SubscriptionSeat & {
      advisorEmail: string | null;
      advisorName: string | null;
    }
  >;
  isOwner: boolean;
  ownerInfo: {
    advisorName: string | null;
    advisorEmail: string | null;
    organization: string | null;
  } | null;
  currentAdvisorAssignedDateTime: Date | null;
  currentAdvisorExpiresDateTime: Date | null;
  pendingSeatInvitations: PendingSeatInvitation[] | null;
};

export const subscriptionSeatsKeys = {
  all: ['subscriptionSeats'] as const,
  byAdvisor: (advisorId: string) => [...subscriptionSeatsKeys.all, advisorId] as const,
};

export const subscriptionSeatsApi = {
  getByAdvisorId: (advisorId: string) =>
    WaterlilyClient.get<SubscriptionSeatsResponse>(`/subscription-seats/${advisorId}`),
};

export const advisorConsentsKeys = {
  all: ['advisorConsents'] as const,
  byAdvisor: (advisorId: string) => [...advisorConsentsKeys.all, advisorId] as const,
};

export type AdvisorConsent = {
  advisorConsentId: string;
  advisorId: string;
  consentText: string;
  consentVersionDescription: string;
  consentDateTime: string;
  ipAddress?: string | null;
  userAgent?: string | null;
};

export type PutAdvisorConsentsRequest = {
  advisorId: string;
  consents: Array<{
    consentText: string;
    consentVersionDescription: string;
    ipAddress?: string | null;
    userAgent?: string | null;
  }>;
};

export type PutAdvisorConsentsResponse = {
  success: boolean;
  message: string;
  results: AdvisorConsent[];
};

export const advisorConsentsApi = {
  getByAdvisorId: (advisorId: string) =>
    WaterlilyClient.get<AdvisorConsent[]>(`/advisor-consents/${advisorId}`),

  saveConsents: (data: PutAdvisorConsentsRequest) =>
    WaterlilyClient.put<PutAdvisorConsentsResponse>(`/advisor-consents/${data.advisorId}`, data),
};

export const pdfReportApi = {
  emailToAdvisor: (
    advisorId: string,
    data: {
      pdfBase64: string;
      clientEmail: string;
      clientName: string;
      filename: string;
      subject?: string;
    },
  ) => WaterlilyClient.post<void>(`/advisor/${advisorId}/pdf-report-email`, data),
};

export const shortFormIntakeKeys = createQueryKeys('shortFormIntake');
export const licenseKeys = createQueryKeys('licenses');

export const shortFormIntakeApi = {
  create: (data: ShortFormIntakeRequest) =>
    WaterlilyClient.post<ShortFormIntakeResponse>('/short-form-intake', data),
  createCouple: (data: ShortFormCoupleIntakeRequest) =>
    WaterlilyClient.post<ShortFormCoupleIntakeResponse>('/short-form-couple-intake', data),
};

export const partnerKeys = createQueryKeys('partner');

export const partnerApi = {
  addPartner: (clientId: string, data: PostAddPartnerRequest) =>
    WaterlilyClient.post<PostAddPartnerResponse>(`/client/${clientId}/add-partner`, data),
  invitePartner: (data: PutPartnerLinkRequestEmailProps) =>
    WaterlilyClient.put<void>('/partner-link-request-email', data),
};

export const quickEditApi = {
  save: (clientId: string, fields: Record<string, string | number | null>) =>
    WaterlilyClient.put<QuickEditIntakeResponse>(`/client/${clientId}/quick-edit-intake`, {
      clientId,
      fields,
    }),
};

export const docusignInspectorKeys = createQueryKeys('docusignInspector');

export type DocusignTabsPreviewParams = {
  formId: string;
  clientPolicyApplicationId: string;
  forClientId: string;
  policyType?: string;
};

export type DocusignTabsPreviewResponse = {
  tabs: {
    textTabs: { tabLabel: string; value: string }[];
    checkboxTabs: { tabLabel: string; selected: 'true' | 'false' }[];
    radioGroupTabs: {
      groupName: string;
      radios: { value: string; selected: 'true' | 'false' }[];
    }[];
  };
  overflowFields: { questionRef: string; value: string }[];
  questionTitles: Record<string, string>;
};

export const docusignInspectorApi = {
  getTabsPreview: (clientId: string, params: DocusignTabsPreviewParams) =>
    WaterlilyClient.get<DocusignTabsPreviewResponse>(
      `/client/${clientId}/docusign-tabs-preview`,
      params,
    ),
  getTemplateTabs: (formId: string) =>
    WaterlilyClient.get<DocusignTemplateTabsResponse>(`/docusign-template-tabs/${formId}`),
};

export const quoteWaitlistApi = {
  signup: (data: API.QuoteWaitlistSignupRequest) =>
    WaterlilyClient.post<{ id: string }>('/quote-waitlist-signup', data),
};

export const anonymousPuiApi = {
  sendRequest(clientId: string, options: SendAnonymousPuiOptions): Promise<AnonymousPuiResponse> {
    const payload = {
      clientId,
      policies: options.policies,
      sendToSelf: options.sendToSelf,
    };

    return WaterlilyClient.post<AnonymousPuiResponse>(
      `/client/${clientId}/anonymous-pui-request`,
      payload,
    );
  },
};

export const intakeDetectionKeys = createQueryKeys('intakeDetection');

export const intakeDetectionApi = {
  getIntakeDetection(clientId: string): Promise<IntakeDetectionResponse> {
    return WaterlilyClient.get<IntakeDetectionResponse>(`/client/${clientId}/intake-type`);
  },
};

export const openAiIntegrationKeys = createQueryKeys('openAiIntegration');

export const openAiIntegrationApi = {
  fetchCompletion: (requestId: string) =>
    WaterlilyClient.get<OpenAiCompletionResponseDbRecord>(`/open-ai-completion/${requestId}`),
  triggerUnderwriting: (requestBody: TriggerUnderwritingRequest) =>
    WaterlilyClient.post<string>(`/trigger-underwriting`, requestBody),
};

export const underwritingResultsKeys = createQueryKeys('underwritingResults');

// =====================================================
// Underwriting Results API
// =====================================================

export const underwritingApi = {
  /**
   * Fetch cached underwriting eligibility results for all products.
   * Returns a map of productName to eligibility status.
   */
  getResults: (clientId: string) =>
    WaterlilyClient.get<UnderwritingResultsResponse>(`/client/${clientId}/underwriting-results`),
};

export type DocusignTemplateTabEntry = {
  tabLabel: string;
  required: string;
  locked: string;
};

export type DocusignTemplateRadioGroupEntry = {
  groupName: string;
  radios: { value: string }[];
};

export type DocusignTemplateSignerTabs = {
  textTabs: DocusignTemplateTabEntry[];
  checkboxTabs: DocusignTemplateTabEntry[];
  radioGroupTabs: DocusignTemplateRadioGroupEntry[];
  signHereTabs: { tabLabel: string }[];
  initialHereTabs: { tabLabel: string }[];
  dateTabs: { tabLabel: string; required: string }[];
  fullNameTabs: { tabLabel: string }[];
};

export type DocusignTemplateSignerResponse = {
  recipientId: string;
  roleName: string;
  tabs: DocusignTemplateSignerTabs;
};

export type DocusignTemplateTabsResponse = {
  templateId: string;
  templateName: string;
  signers: DocusignTemplateSignerResponse[];
  templates?: { templateId: string; templateName: string }[];
};

export type TriggerUnderwritingRequest = {
  clientId: string;
  fundingPolicyType: FundingPolicyType;
  formData?: PolicyFormSubmissionData;
};
